import { useState } from "react";
import { useReactFlow } from "@xyflow/react";
import useNodeAdd from "./useNodeAdd";

import {
  ConfigurationPanel,
  ConfigurationPanelHeader,
  ConfigurationPanelBody,
  ConfigurationPanelFooter,
} from "../Components/ConfigurationPanel";
import { Button } from "V2.0/common/button";
import {
  getValidNewNodesToAdd,
  getValidNewNodesToInsert,
} from "../Models/nodeCatalog";
import NodeCatalogList from "../Components/NodeCatalogList";
import { usePipelineBuilder } from "V2.0/HomePages/Pipelines/PipelineBuilder/PipelineBuilderProvider";
import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import { AssistantNodeAction, NodeType } from "../Models/NodeType";
import ConditionRulesList from "../Components/ConditionRulesList";
import VoiceCallActionResults from "./BranchSelection/Assistant/VoiceCallAction";
import { DelayMode } from "../Models/Delay";
import DelayUntilTimestampSkipIfPast from "./BranchSelection/Delay";

export default NodeAdd;

function NodeAdd() {
  const { getEdges, getNode } = useReactFlow();
  const { panel, closePanel, onConfigureNode } = usePipelineBuilder();
  const [selectedBranch, setSelectedBranch] = useState(null);

  const sourceNode = panel.sourceNode;
  const { addNode } = useNodeAdd(sourceNode, selectedBranch, onConfigureNode);

  const isConditional = sourceNode.type === NodeType.CONDITIONAL;
  const isVoiceAssistantAction =
    sourceNode.type === NodeType.ASSISTANT &&
    sourceNode.data.action === AssistantNodeAction.VOICE_CALL;
  const isDelayUntilTimestampWithSkipIfPast =
    sourceNode.type === NodeType.DELAY &&
    sourceNode.data.delayMode === DelayMode.UNTIL_TIMESTAMP &&
    sourceNode.data.skipIfPast;

  const showCatalog =
    (!isConditional &&
      !isVoiceAssistantAction &&
      !isDelayUntilTimestampWithSkipIfPast) ||
    selectedBranch;

  const sourcePort = selectedBranch?.outputPort ?? "default";
  const existingEdge = getEdges().find(
    (edge) =>
      edge.source === sourceNode.id &&
      (edge.data?.sourcePort ?? "default") === sourcePort,
  );

  const targetNode = existingEdge ? getNode(existingEdge.target) : null;

  const validNodes = targetNode
    ? getValidNewNodesToInsert(sourceNode, targetNode)
    : getValidNewNodesToAdd(sourceNode);

  const title = isConditional && !selectedBranch ? "Select Branch" : "Add Step";

  return (
    <ConfigurationPanel>
      <ConfigurationPanelHeader>
        <div className="flex items-center gap-2">
          {isConditional && selectedBranch && (
            <button onClick={() => setSelectedBranch(null)}>
              <ChevronLeftIcon className="size-4" />
            </button>
          )}
          <p>{title}</p>
          {isConditional && selectedBranch && (
            <span className="text-sm text-zinc-500">
              · {selectedBranch.outputPort}
            </span>
          )}
        </div>
      </ConfigurationPanelHeader>
      <ConfigurationPanelBody>
        {isConditional && !selectedBranch && (
          <ConditionRulesList
            node={sourceNode}
            onSelect={setSelectedBranch}
          />
        )}
        {isVoiceAssistantAction && !selectedBranch && (
          <VoiceCallActionResults
            node={sourceNode}
            onSelect={setSelectedBranch}
          />
        )}
        {isDelayUntilTimestampWithSkipIfPast && !selectedBranch && (
          <DelayUntilTimestampSkipIfPast
            node={sourceNode}
            onSelect={setSelectedBranch}
          />
        )}
        {showCatalog && (
          <NodeCatalogList
            nodes={validNodes}
            onSelect={addNode}
          />
        )}
      </ConfigurationPanelBody>
      <ConfigurationPanelFooter>
        <div className="flex justify-end gap-x-2">
          <Button
            outline
            onClick={closePanel}
          >
            Cancel
          </Button>
        </div>
      </ConfigurationPanelFooter>
    </ConfigurationPanel>
  );
}
